import { CalendarCheck, Car, Heart } from "lucide-react";

const steps = [
  {
    number: "1",
    icon: CalendarCheck,
    title: "Book Appointment",
    description: "Schedule your visit online or give us a call. Choose a time that works best for you and your pet.",
    color: "sky",
  },
  {
    number: "2",
    icon: Car,
    title: "Bring Your Pet or Choose Mobile Service",
    description: "Drop off your furry friend at our facility or opt for our convenient mobile grooming service.",
    color: "amber",
  },
  {
    number: "3",
    icon: Heart,
    title: "Pick Up a Clean & Happy Pet",
    description: "Collect your pampered pet looking and feeling their best. Enjoy the fresh, clean results!",
    color: "emerald",
  },
];

const colorClasses = {
  sky: {
    bg: "bg-sky-100",
    number: "bg-sky-500 text-white",
    icon: "text-sky-600",
  },
  amber: {
    bg: "bg-amber-100",
    number: "bg-amber-500 text-white",
    icon: "text-amber-600",
  },
  emerald: {
    bg: "bg-emerald-100",
    number: "bg-emerald-500 text-white",
    icon: "text-emerald-600",
  },
};

export const HowItWorksSection = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-16 md:py-24 bg-white"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            How It Works
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Getting your pet groomed has never been easier. Follow these simple steps.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => {
            const colors = colorClasses[step.color];
            const Icon = step.icon;

            return (
              <div
                key={index}
                data-testid={`step-${step.number}`}
                className="relative step-card pt-8 animate-fade-in-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Step Number */}
                <div
                  className={`step-number ${colors.number} shadow-lg`}
                >
                  {step.number}
                </div>

                <div className={`${colors.bg} rounded-2xl p-8 pt-12 h-full hover-lift`}>
                  <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-8 h-8 ${colors.icon}`} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Connector line (hidden on last item and mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-6 w-12 border-t-2 border-dashed border-slate-300"></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Safe Handling Note */}
        <div className="mt-16 text-center">
          <div
            data-testid="safe-handling-note"
            className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-50 rounded-full border border-emerald-100"
          >
            <Heart className="w-5 h-5 text-emerald-600" />
            <span className="text-emerald-800 font-medium">
              We emphasize safe handling and gentle care for all pets
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
